(function() {
  'use strict';

const hasVisited = () => {
  const visited = localStorage.getItem('navigation_thumbnail_visited');
  return !!visited; // returns true if flag exists (user has visited before)
};

const detectUserLeaving = () => {
  const INTERNAL_NAV_KEY = 'nav_thumb_internal_nav';
  const LAST_ACTIVITY_KEY = 'nav_thumb_last_activity';
  
  // Clear any stale internal navigation flag from previous page
  // If we just navigated internally, this flag was set on previous page
  const wasInternalNav = sessionStorage.getItem(INTERNAL_NAV_KEY);
  if (wasInternalNav) {
    sessionStorage.removeItem(INTERNAL_NAV_KEY);
  }
  
  // Update last activity timestamp
  const updateActivity = () => {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  };
  updateActivity();
  
  // Track clicks on internal links to prevent setting visited flag
  // Use capture phase (true) to catch events before other handlers
  document.addEventListener('click', (e) => {
    const target = e.target;
    const link = target.closest('a');
    const roleLink = target.closest('[role="link"]');
    
    // Handle role="link" divs (used by Replo and other page builders)
    if (roleLink && !link) {
      sessionStorage.setItem(INTERNAL_NAV_KEY, Date.now().toString());
      updateActivity();
      // Don't return - let event propagate so Replo's JS can handle navigation
    }
    
    if (!link) {
      return;
    }
    
    // Try to find href from various sources
    let href = link.href || link.getAttribute('data-href') || link.getAttribute('href');
    
    if (!href) {
      return;
    }
    
    try {
      const linkUrl = new URL(href);
      const currentUrl = new URL(window.location.href);
      const isInternal = linkUrl.hostname === currentUrl.hostname;
      
      // If clicking internal link, set flag that persists to next page
      if (isInternal) {
        sessionStorage.setItem(INTERNAL_NAV_KEY, Date.now().toString());
        updateActivity();
      }
    } catch (e) {
      // Invalid URL, ignore
    }
  }, true); // Capture phase ensures we catch it early
  
  // Additional safety: track ANY navigation that looks internal
  // This catches cases where click event doesn't bubble to our handler
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function() {
    sessionStorage.setItem(INTERNAL_NAV_KEY, Date.now().toString());
    return originalPushState.apply(history, arguments);
  };
  
  history.replaceState = function() {
    sessionStorage.setItem(INTERNAL_NAV_KEY, Date.now().toString());
    return originalReplaceState.apply(history, arguments);
  };
  
  // Track back/forward button navigation (popstate event)
  window.addEventListener('popstate', () => {
    sessionStorage.setItem(INTERNAL_NAV_KEY, Date.now().toString());
    updateActivity();
  });
  
  // Track Safari bfcache restoration (pageshow event)
  // When Safari restores from bfcache (back/forward nav), persisted=true
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      // Page restored from bfcache - this is internal navigation
      sessionStorage.setItem(INTERNAL_NAV_KEY, Date.now().toString());
      updateActivity();
    }
  });
  
  // Helper to check if this is internal navigation happening right now
  const isInternalNavigation = () => {
    const flag = sessionStorage.getItem(INTERNAL_NAV_KEY);
    
    if (!flag) {
      return false;
    }
    
    // Check if flag was set recently (within last 3 seconds)
    const timestamp = parseInt(flag, 10);
    const now = Date.now();
    const timeDiff = now - timestamp;
    const isRecent = timeDiff < 3000;
    
    if (isRecent) {
      return true;
    }
    return false;
  };
  
  // Helper to mark user as having left
  const markAsVisited = (reason) => {
    if (isInternalNavigation()) {
      return false;
    }
    
    localStorage.setItem('navigation_thumbnail_visited', 'true');
    return true;
  };
  
  // 1. beforeunload - fires before page unload (works on desktop)
  window.addEventListener('beforeunload', (e) => {
    markAsVisited('beforeunload - User closing tab/leaving site');
  });
  
  // 2. pagehide - Critical for Safari iOS/Mac (most reliable for all cases)
  window.addEventListener('pagehide', (e) => {
    // For Safari: if page is being cached (persisted=true), it's likely bfcache internal nav
    // If persisted=false, page is truly unloading
    if (e.persisted === false) {
      markAsVisited('pagehide (persisted=false) - User leaving');
    }
  });
  
  // 3. visibilitychange - Backup for mobile browsers where beforeunload doesn't fire
  let visibilityHiddenTime = null;
  let visibilityTimeout = null;
  
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      visibilityHiddenTime = Date.now();
      
      // Clear any existing timeout
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
      
      // Set a delayed check - if page is still hidden after 2 seconds, likely leaving
      visibilityTimeout = setTimeout(() => {
        // Double-check we're still hidden and this isn't internal navigation
        if (document.visibilityState === 'hidden' && visibilityHiddenTime && !isInternalNavigation()) {
          const hiddenDuration = Date.now() - visibilityHiddenTime;
          
          if (hiddenDuration >= 1900) { // Still hidden after ~2 seconds
            markAsVisited('visibilitychange - Page hidden for 2s, user leaving');
          }
        }
        visibilityTimeout = null;
      }, 2000);
    } else if (document.visibilityState === 'visible') {
      // Page became visible again - cancel the leaving detection
      
      // Clear the timeout to prevent it from firing
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
        visibilityTimeout = null;
      }
      
      visibilityHiddenTime = null;
      updateActivity();
    }
  });
};

// Set flag when user leaves the site
detectUserLeaving();

const waitForElement = (selector, callback, { intervalMs = 200, maxTimeMs = 10000 } = {}) => {
  const start = Date.now();
  const interval = setInterval(() => {
    const el = document.querySelector(selector);
    if (el) {
      clearInterval(interval);
      callback(el);
      return;
    }
    if (Date.now() - start >= maxTimeMs) clearInterval(interval);
  }, intervalMs);
};

// Hide for returning visitors (after they've left and come back)
if (hasVisited()) {
  waitForElement('.navigation-thumbnail', (navThumbnail) => {
    navThumbnail.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important;';
    // Also hide the parent section
    const section = navThumbnail.closest('section');
    if (section) {
      section.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important;';
    }
  });
}

waitForElement("#qab_container", (qab) => {
  // Get ALL header sections, not just the first one
  const headerSections = document.querySelectorAll("section.shopify-section-group-header-group");
  if (!headerSections.length) return;

  // Fix overflow issues on ancestor elements that break sticky positioning
  const fixOverflowOnAncestors = () => {
    headerSections.forEach(section => {
      let el = section.parentElement;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        if (style.overflow === 'hidden' || style.overflowY === 'hidden' || style.overflowX === 'hidden') {
          el.style.overflow = 'visible';
        }
        el = el.parentElement;
      }
    });
  };

  const apply = () => {
    const height = Math.ceil(qab.getBoundingClientRect().height);

    headerSections.forEach(section => {
      section.style.position = "sticky";
      section.style.top = `${height}px`;
      section.style.zIndex = "3";
    });
  };

  // Fix ancestors first
  fixOverflowOnAncestors();

  // Initial apply
  apply();

  // Keep in sync if qab changes (e.g., async content, responsive)
  let rafId = null;
  const scheduleApply = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(apply);
  };

  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(scheduleApply);
    ro.observe(qab);
  } else {
    // fallback
    window.addEventListener("resize", scheduleApply);
  }
});

})(); // End of IIFE - prevents global scope pollution
